import { getDebateThumbnail } from "components/templates/common/Discuss/util";
import Head from "next/head";
import { filterMarkdown } from "utils/format";

function DiscussDetailMetaHead({
  detailDiscussData,
}: {
  detailDiscussData: any;
}) {
  const title = detailDiscussData?.title;
  const intro = detailDiscussData?.intro;
  const description = detailDiscussData?.description;
  const keywords = detailDiscussData?.keywords;
  const url = detailDiscussData?.url_slug
    ? `${detailDiscussData?.url_slug}-${detailDiscussData?.id}`
    : detailDiscussData?.id;

  // JSON-LD 구조화된 데이터 생성
  const generateStructuredData = (): string => {
    if (!detailDiscussData) return "{}";
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://riskweather.io/ko/discuss/${url}`,
          },
          headline: title,
          description: filterMarkdown(description),
          image:
            detailDiscussData?.thumbnail_url ||
            `https://riskweather.io${getDebateThumbnail(
              detailDiscussData?.thumbnail_url,
              detailDiscussData?.id
            )}`,
          author: {
            "@type": "Person",
            name: "RiskWeather",
            url: `https://riskweather.io/ko/discuss/${url}`,
          },
          publisher: {
            "@type": "Organization",
            name: "RiskWeather",
            url: `https://riskweather.io/ko/discuss/${url}`,
            logo: {
              "@type": "ImageObject",
              url: "https://riskweather.io/images/header/headerLogo.svg",
              width: 146,
              height: 24,
            },
          },
          datePublished: detailDiscussData?.created_at,
          dateModified: detailDiscussData?.updated_at,
          url: `https://riskweather.io/ko/discuss/${url}`,
          inLanguage: "ko-KR",
          isAccessibleForFree: true,
          genre: ["주식", "분석", "뉴스", "정치", "사회", "토론", "의견"],
          about: [
            {
              "@type": "Thing",
              name: title,
              description: filterMarkdown(description),
            },
            ...(keywords?.midkeywords?.length > 0
              ? keywords.midkeywords.map((keyword: any) => ({
                  "@type": "Thing",
                  name: keyword,
                }))
              : []),
          ],
          articleSection: "리포트",
          articleBody: detailDiscussData?.content,
          keywords: keywords
            ? keywords?.midkeywords?.join(", ") +
              "," +
              keywords?.longkeywords?.join(", ")
            : "",
          copyrightYear: new Date().getFullYear(),
          copyrightHolder: {
            "@type": "Organization",
            name: "RiskWeather",
          },
          isPartOf: {
            "@type": "WebSite",
            name: "리스크웨더",
            url: "https://riskweather.io",
          },
          ...(detailDiscussData?.relatedStock?.length > 0 && {
            mentions: [
              ...(detailDiscussData?.relatedStock?.length > 0
                ? detailDiscussData.relatedStock.map((tag: any) => ({
                    "@type": "Thing",
                    name: tag?.ITEM_KR_NM,
                  }))
                : []),
            ],
          }),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "홈",
              item: `https://riskweather.io/ko`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "토론",
              item: `https://riskweather.io/ko/discuss`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: title || "토론",
              item: `https://riskweather.io/ko/discuss/${url}`,
            },
          ],
        },
      ],
    };

    return JSON.stringify(structuredData, null, 2);
  };

  return (
    <Head>
      <link rel="Icon" href="/images/favicon.png" type="image/x-icon" />
      <title>{title ? title : "리스크웨더"}</title>

      <meta
        property="og:image"
        content={
          detailDiscussData?.thumbnail_url
            ? detailDiscussData?.thumbnail_url
            : getDebateThumbnail(
                detailDiscussData?.thumbnail_url,
                detailDiscussData?.id
              )
            ? `${process.env.NEXTAUTH_URL}${getDebateThumbnail(
                detailDiscussData?.thumbnail_url,
                detailDiscussData?.id
              )}`
            : `https://riskweather.io/images/Thumb.png`
        }
        key="og:image"
      />

      <meta
        property="og:description"
        content={intro ? filterMarkdown(intro) : ""}
        key="og:description"
      />
      <meta
        name="description"
        content={description ? filterMarkdown(description) : ""}
      />
      <meta
        property="og:title"
        content={title ? title : "리스크웨더"}
        key="og:title"
      />
      <meta name="twitter:title" content={title ? title : "리스크웨더"} />
      <meta name="author" content="RiskWeather Team" />
      <meta name="twitter:description" content={intro ? intro : ""} />
      <meta
        name="twitter:image"
        content={`https://riskweather.io/images/DetailThumb.png`}
      />
      <meta name="twitter:card" content={`summary`} />

      {/* JSON-LD 구조화된 데이터 */}
      {detailDiscussData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: generateStructuredData(),
          }}
        />
      )}
    </Head>
  );
}

export default DiscussDetailMetaHead;
